/**
 * plateLayout — how much of the window the painting gets.
 *
 * Pulled out of `C5Frame.layoutInner` so it can be tested without a DOM. It is
 * small arithmetic, but it decides the single most visible thing about the
 * game: whether the scene fills the screen or sits in a box with a console
 * around it.
 *
 * Two passes, and the second one matters. The first solves for a scale as if
 * the bands could shrink freely with the plate; the second re-derives the band
 * heights from that scale *after* clamping it, because a clamped scale means
 * the bands are no longer proportional to the plate and the naive single-pass
 * answer overflows the window.
 */

/** Design width the band metrics were authored against. */
export const DESIGN_PLATE_WIDTH = 1000;
export const BAND_TOP_BASE = 46;
export const BAND_BOTTOM_BASE = 88;
export const MIN_SCALE = 0.55;
export const MAX_SCALE = 1.5;
export const PLATE_ASPECT = 16 / 9;
/** Hairline border on #c5-frame, per edge, when the console is drawn. */
export const FRAME_BORDER_PX = 1;

/**
 * How thick the frame's own edge is in a given mode.
 *
 * A bare frame draws no border, so it must not reserve one either — otherwise
 * the plate comes out two pixels short and the "no chrome" scene sits inside a
 * hairline of the console it was supposed to have shed.
 */
export function frameBorderPx(chrome: PlateChrome): number {
  return chrome === 'bare' ? 0 : FRAME_BORDER_PX;
}

/** How much console is drawn around the painting. */
export type PlateChrome = 'full' | 'bare';

export interface PlateMetrics {
  scale: number;
  bandTop: number;
  bandBottom: number;
  plateW: number;
  plateH: number;
  /** Hairline border drawn per edge in this mode. 0 when bare. */
  borderPx: number;
  /** Total frame height including bands and both hairline borders. */
  frameH: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * @param winW  window width in px.
 * @param winH  window height in px — the border is subtracted here, so callers
 *              hand over the raw viewport and do not have to know which mode
 *              has an edge and which does not.
 * @param chrome `bare` removes the bands entirely, and their height with them,
 *               so the painting gets that space rather than leaving a black
 *               margin where the console used to be.
 */
export function solvePlate(winW: number, winH: number, chrome: PlateChrome = 'full'): PlateMetrics {
  const bare = chrome === 'bare';
  const border = frameBorderPx(chrome);
  const availW = winW;
  const availH = winH - border * 2;
  const topBase = bare ? 0 : BAND_TOP_BASE;
  const bottomBase = bare ? 0 : BAND_BOTTOM_BASE;

  // First pass: solve for the plate width if the bands scaled freely.
  const bandsPerPlateWidth = (topBase + bottomBase) / DESIGN_PLATE_WIDTH;
  const guess = Math.min(availW, availH / (1 / PLATE_ASPECT + bandsPerPlateWidth));
  const scale = clamp(guess / DESIGN_PLATE_WIDTH, MIN_SCALE, MAX_SCALE);

  // Second pass: with the clamped scale, the bands have a fixed height.
  const bandTop = Math.round(topBase * scale);
  const bandBottom = Math.round(bottomBase * scale);
  const plateW = Math.max(
    200,
    Math.floor(Math.min(availW, (availH - bandTop - bandBottom) * PLATE_ASPECT)),
  );
  const plateH = Math.round(plateW / PLATE_ASPECT);

  return {
    scale,
    bandTop,
    bandBottom,
    plateW,
    plateH,
    borderPx: border,
    frameH: plateH + bandTop + bandBottom + border * 2,
  };
}
